import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import DriveSidebar from '@/components/feature/DriveSidebar';
import Breadcrumb from './components/Breadcrumb';
import Toolbar from './components/Toolbar';
import FileGrid from './components/FileGrid';
import FileList from './components/FileList';
import ContextMenu from './components/ContextMenu';
import DeleteDialog from './components/DeleteDialog';
import BulkDeleteDialog from './components/BulkDeleteDialog';
import NewFolderDialog from './components/NewFolderDialog';
import RenameDialog from './components/RenameDialog';
import UploadZone from './components/UploadZone';
import ImageViewer from './components/ImageViewer';
import type { LightboxImage } from './components/ImageViewer';
import MediaPlayer from './components/MediaPlayer';
import type { MediaItem } from './components/MediaPlayer';
import PdfViewer from './components/PdfViewer';
import type { PdfItem } from './components/PdfViewer';
import Toast from './components/Toast';
import type { ToastData } from './components/Toast';
import type { SortField, SortOrder } from './components/Toolbar';
import { mockFiles, totalStorage as mockTotalStorage, usedStorage as mockUsedStorage } from '@/mocks/driveFiles';
import type { DriveItem } from '@/mocks/driveFiles';
import {
  fetchAllFiles,
  fetchTrash as fetchTrashApi,
  deleteItem,
  moveItem,
  renameItem,
  createFolder as createFolderApi,
  uploadFiles,
  uploadFolder,
  restoreTrashItem,
  permanentlyDeleteTrashItem,
  emptyTrash as emptyTrashApi,
  restoreAllTrash as restoreAllTrashApi,
  getDownloadUrl,
  getPreviewUrl,
  fetchStats,
  type UploadItem,
} from '@/services/driveApi';

const TRASH_FOLDER_ID = '__trash__';
const TRASH_RETENTION_DAYS = 30;

const mockLightboxPreviews: Record<string, string> = {
  '여행_사진_001.jpg':
    'https://readdy.ai/api/search-image?query=Stunning%20travel%20landscape%20photography%20with%20vibrant%20saturated%20colors%2C%20crystal%20clear%20mountain%20lake%20reflecting%20dramatic%20alpine%20peaks%20under%20bright%20blue%20sky%2C%20professional%20nature%20photography%2C%20ultra%20sharp%20details%2C%20warm%20golden%20hour%20lighting%2C%20minimal%20editing%2C%20breathtaking%20scenery%20with%20pine%20trees%20and%20wildflowers%20in%20foreground&width=1600&height=1200&seq=lb-travel-001&orientation=landscape',
  '프로필_사진.png':
    'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20photography%2C%20clean%20studio%20lighting%20with%20soft%20diffused%20shadows%2C%20simple%20light%20neutral%20background%2C%20warm%20natural%20expression%2C%20confident%20and%20approachable%20look%2C%20minimalist%20aesthetic%2C%20high%20quality%20editorial%20portrait%2C%20sharp%20focus%20on%20eyes%2C%20subtle%20catchlights&width=1200&height=1600&seq=lb-profile-001&orientation=portrait',
  '제품_샘플.webp':
    'https://readdy.ai/api/search-image?query=Modern%20minimalist%20product%20photography%2C%20elegant%20ceramic%20vase%20on%20clean%20white%20marble%20surface%2C%20soft%20natural%20window%20lighting%20from%20left%20side%2C%20subtle%20warm%20shadows%2C%20commercial%20product%20shot%20style%2C%20high%20detail%20texture%2C%20neutral%20cream%20and%20beige%20color%20palette%2C%20sophisticated%20aesthetic%2C%20shallow%20depth%20of%20field&width=1600&height=1200&seq=lb-product-001&orientation=landscape',
  '인테리어_참고.jpg':
    'https://readdy.ai/api/search-image?query=Modern%20Scandinavian%20interior%20design%20living%20room%2C%20warm%20oak%20wood%20flooring%2C%20natural%20sunlight%20streaming%20through%20large%20windows%2C%20minimalist%20furniture%20with%20organic%20textures%2C%20cozy%20neutral%20color%20palette%2C%20indoor%20plants%2C%20architectural%20digest%20style%20photography%2C%20harmonious%20composition%2C%20soft%20ambient%20lighting%2C%20serene%20atmosphere&width=1600&height=1200&seq=lb-interior-001&orientation=landscape',
  '로고_디자인_최종.png':
    'https://readdy.ai/api/search-image?query=Modern%20minimalist%20brand%20identity%20logo%20design%20presentation%2C%20clean%20geometric%20logomark%20centered%20on%20white%20background%2C%20sophisticated%20typography%20pairing%2C%20professional%20branding%20mockup%2C%20flat%20design%20aesthetic%2C%20elegant%20corporate%20identity%2C%20subtle%20gradient%20accent%20lines%2C%20high%20resolution%2C%20gallery%20wall%20presentation%20style&width=1600&height=1200&seq=lb-logo-001&orientation=landscape',
  '스크린샷_20260806.png':
    'https://readdy.ai/api/search-image?query=Clean%20modern%20web%20application%20dashboard%20interface%20screenshot%2C%20light%20mode%20UI%20design%20with%20soft%20shadows%2C%20elegant%20data%20visualization%20cards%20and%20charts%2C%20minimalist%20admin%20panel%20layout%2C%20professional%20SaaS%20product%20interface%2C%20white%20and%20warm%20gray%20background%2C%20organized%20grid%20layout%2C%20refined%20typography%2C%20subtle%20pastel%20accent%20colors&width=1600&height=1200&seq=lb-screenshot-001&orientation=landscape',
};

const mockMediaSources: Record<string, string> = {
  '튜토리얼_영상.mp4':
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
  '발표_녹화.mov':
    'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
  'bgm_작업용.mp3':
    'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
};

export default function Home() {
  // --- Data states ---
  const [allItems, setAllItems] = useState<DriveItem[]>([]);
  const [trashItems, setTrashItems] = useState<DriveItem[]>([]);
  const [usedStorage, setUsedStorage] = useState(0);
  const [totalStorage, setTotalStorage] = useState(mockTotalStorage);
  const [apiAvailable, setApiAvailable] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // --- UI states ---
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    item: DriveItem;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DriveItem | null>(null);
  const [deleteTargets, setDeleteTargets] = useState<DriveItem[] | null>(null);
  const [renameTarget, setRenameTarget] = useState<DriveItem | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [mediaIndex, setMediaIndex] = useState<number | null>(null);
  const [pdfIndex, setPdfIndex] = useState<number | null>(null);

  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [draggedItems, setDraggedItems] = useState<DriveItem[]>([]);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [dragOverBreadcrumbId, setDragOverBreadcrumbId] = useState<string | null>(null);
  const [dragOverSidebarId, setDragOverSidebarId] = useState<string | null>(null);
  const dragCounter = useRef(0);
  const mainContentRef = useRef<HTMLDivElement>(null);

  // --- Data fetching ---

  const loadData = useCallback(async (showLoading = false) => {
    if (showLoading) setIsLoading(true);
    else setIsRefreshing(true);

    try {
      const [items, trash, stats] = await Promise.all([
        fetchAllFiles(),
        fetchTrashApi(),
        fetchStats(),
      ]);
      setAllItems(items);
      setTrashItems(trash);
      setUsedStorage(stats.usedStorage);
      setTotalStorage(stats.totalStorage);
      if (apiAvailable !== true) setApiAvailable(true);
    } catch (err) {
      console.warn('API unreachable, using mock data:', err);
      if (apiAvailable !== false) {
        setApiAvailable(false);
        setAllItems([...mockFiles]);
        setTrashItems([]);
        setUsedStorage(mockUsedStorage);
        setTotalStorage(mockTotalStorage);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [apiAvailable]);

  useEffect(() => {
    loadData(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-purge items older than 30 days (only for mock mode)
  useEffect(() => {
    if (apiAvailable !== false) return;
    const now = new Date();
    const expiryDate = new Date(now.getTime() - TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000);
    const expiredIds = new Set(
      allItems
        .filter((f) => f.deletedAt && new Date(f.deletedAt) < expiryDate)
        .map((f) => f.id)
    );
    if (expiredIds.size > 0) {
      setAllItems((prev) => prev.filter((f) => !expiredIds.has(f.id)));
    }
  }, [apiAvailable]);

  // --- Helpers ---

  const addToast = useCallback((message: string, type: ToastData['type'] = 'success') => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const files = allItems;
  const isTrashView = currentFolderId === TRASH_FOLDER_ID;

  const getBreadcrumbPath = (): { id: string | null; name: string }[] => {
    if (isTrashView) return [{ id: TRASH_FOLDER_ID, name: '휴지통' }];
    const path: { id: string | null; name: string }[] = [{ id: null, name: '내 드라이브' }];
    if (!currentFolderId) return path;
    let folderId: string | null = currentFolderId;
    const segments: { id: string | null; name: string }[] = [];
    while (folderId) {
      const folder = files.find((f) => f.id === folderId && f.type === 'folder' && !f.deletedAt);
      if (!folder) break;
      segments.unshift({ id: folder.id, name: folder.name });
      folderId = folder.parentId;
    }
    return [...path, ...segments];
  };

  const getDescendantIds = useCallback((folderId: string): string[] => {
    const ids: string[] = [folderId];
    const children = files.filter((f) => f.parentId === folderId && !f.deletedAt);
    children.forEach((child) => {
      if (child.type === 'folder') {
        ids.push(...getDescendantIds(child.id));
      } else {
        ids.push(child.id);
      }
    });
    return ids;
  }, [files]);

  const itemPath = (item: DriveItem): string => item.path ?? '';

  // === Trash Actions (API-first) ===

  const restoreFromTrash = useCallback(async (item: DriveItem) => {
    if (apiAvailable === true) {
      try {
        await restoreTrashItem(item.id);
        await loadData();
        addToast(`'${item.name}' 복구 완료`);
      } catch (err: any) {
        addToast(`복구 실패: ${err.message}`, 'error');
      }
      return;
    }
    // Mock fallback
    setAllItems((prev) =>
      prev.map((f) =>
        f.id === item.id
          ? { ...f, deletedAt: undefined, parentId: f.parentId, updatedAt: new Date().toISOString() }
          : f
      )
    );
    setTrashItems((prev) => prev.filter((t) => t.id !== item.id));
    addToast(`'${item.name}' 복구 완료`);
  }, [apiAvailable, loadData, addToast]);

  const permanentlyDelete = useCallback(async (item: DriveItem) => {
    if (apiAvailable === true) {
      try {
        await permanentlyDeleteTrashItem(item.id);
        await loadData();
        addToast(`'${item.name}' 영구 삭제 완료`);
      } catch (err: any) {
        addToast(`삭제 실패: ${err.message}`, 'error');
      }
      return;
    }
    const getAllDescendantIds = (folderId: string): string[] => {
      const ids: string[] = [folderId];
      const children = files.filter((f) => f.parentId === folderId);
      children.forEach((child) => {
        if (child.type === 'folder') ids.push(...getAllDescendantIds(child.id));
        else ids.push(child.id);
      });
      return ids;
    };
    const idsToRemove = item.type === 'folder'
      ? getAllDescendantIds(item.id)
      : [item.id];
    setAllItems((prev) => prev.filter((f) => !idsToRemove.includes(f.id)));
    setTrashItems((prev) => prev.filter((f) => !idsToRemove.includes(f.id)));
    addToast(`'${item.name}' 영구 삭제 완료`);
  }, [apiAvailable, loadData, addToast, files]);

  const emptyTrash = useCallback(async () => {
    if (apiAvailable === true) {
      try {
        await emptyTrashApi();
        await loadData();
        addToast('휴지통을 비웠습니다');
      } catch (err: any) {
        addToast(`실패: ${err.message}`, 'error');
      }
      return;
    }
    const idsToRemove = new Set(trashItems.map((f) => f.id));
    trashItems.forEach((item) => {
      if (item.type === 'folder') {
        getDescendantIds(item.id).forEach((id) => idsToRemove.add(id));
      }
    });
    setAllItems((prev) => prev.filter((f) => !idsToRemove.has(f.id)));
    setTrashItems([]);
    addToast('휴지통을 비웠습니다');
  }, [apiAvailable, loadData, addToast, trashItems, getDescendantIds]);

  const restoreAllTrash = useCallback(async () => {
    if (apiAvailable === true) {
      try {
        await restoreAllTrashApi();
        await loadData();
        addToast('모든 항목을 복구했습니다');
      } catch (err: any) {
        addToast(`복구 실패: ${err.message}`, 'error');
      }
      return;
    }
    setAllItems((prev) =>
      prev.map((f) =>
        f.deletedAt ? { ...f, deletedAt: undefined, updatedAt: new Date().toISOString() } : f
      )
    );
    setTrashItems([]);
    addToast('모든 항목을 복구했습니다');
  }, [apiAvailable, loadData, addToast]);

  // === Filtering and sorting ===

  const filteredAndSortedItems = useMemo(() => {
    const source = isTrashView ? trashItems : files.filter((f) => f.parentId === currentFolderId && !f.deletedAt);

    let result = [...source];
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter((f) => f.name.toLowerCase().includes(query));
    }

    const folders = result.filter((f) => f.type === 'folder');
    const fileItems = result.filter((f) => f.type === 'file');

    const sortFn = (a: DriveItem, b: DriveItem) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name, 'ko');
          break;
        case 'updatedAt':
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
        case 'size':
          comparison = (a.size ?? 0) - (b.size ?? 0);
          break;
        case 'type': {
          const typeA = a.type === 'folder' ? '' : (a.mimeType ?? '');
          const typeB = b.type === 'folder' ? '' : (b.mimeType ?? '');
          comparison = typeA.localeCompare(typeB);
          break;
        }
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    };

    folders.sort(sortFn);
    fileItems.sort(sortFn);
    return [...folders, ...fileItems];
  }, [files, currentFolderId, searchQuery, sortField, sortOrder, isTrashView, trashItems]);

  const totalFileItems = files.filter((f) => f.type === 'file' && !f.deletedAt);
  const totalFolderItems = files.filter((f) => f.type === 'folder' && !f.deletedAt);

  const imageFiles = useMemo(() => {
    return filteredAndSortedItems.filter((f) => f.type === 'file' && f.mimeType?.startsWith('image/'));
  }, [filteredAndSortedItems]);

  const lightboxImages = useMemo((): LightboxImage[] => {
    return imageFiles.map((item) => ({
      item,
      src: apiAvailable === true && item.path
        ? getDownloadUrl(item.path)
        : (mockLightboxPreviews[item.name] || ''),
    }));
  }, [imageFiles, apiAvailable]);

  const videoAudioFiles = useMemo(() => {
    return filteredAndSortedItems.filter(
      (f) => f.type === 'file' && (f.mimeType?.startsWith('video/') || f.mimeType?.startsWith('audio/'))
    );
  }, [filteredAndSortedItems]);

  const mediaFiles = useMemo((): MediaItem[] => {
    return videoAudioFiles.map((item) => ({
      item,
      src: apiAvailable === true && item.path
        ? getDownloadUrl(item.path)
        : (mockMediaSources[item.name] || ''),
    }));
  }, [videoAudioFiles, apiAvailable]);

  const pdfFiles = useMemo(() => {
    return filteredAndSortedItems.filter(
      (f) => f.type === 'file' && f.mimeType === 'application/pdf'
    );
  }, [filteredAndSortedItems]);

  const pdfItems = useMemo((): PdfItem[] => {
    return pdfFiles.map((item) => ({
      item,
      src: apiAvailable === true && item.path
        ? getPreviewUrl(item.path)
        : '',
    }));
  }, [pdfFiles, apiAvailable]);

  // === Actions ===

  const handleFileClick = (file: DriveItem, e?: React.MouseEvent) => {
    const isCtrlOrCmd = e && (e.ctrlKey || e.metaKey);
    if (isCtrlOrCmd) { handleToggleSelect(file.id, e); return; }
    setSelectedIds(new Set());
    if (file.mimeType?.startsWith('image/')) {
      const idx = imageFiles.findIndex((f) => f.id === file.id);
      if (idx >= 0) setLightboxIndex(idx);
    } else if (file.mimeType?.startsWith('video/') || file.mimeType?.startsWith('audio/')) {
      const idx = videoAudioFiles.findIndex((f) => f.id === file.id);
      if (idx >= 0) setMediaIndex(idx);
    } else if (file.mimeType === 'application/pdf') {
      const idx = pdfFiles.findIndex((f) => f.id === file.id);
      if (idx >= 0) setPdfIndex(idx);
    }
  };

  const handleFolderClick = (folderId: string) => {
    if (isTrashView) return;
    setCurrentFolderId(folderId);
    setSelectedIds(new Set());
    setSearchQuery('');
    setSidebarOpen(false);
  };

  const handleToggleSelect = (itemId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const handleDeleteSelected = () => {
    const targets = files.filter((f) => selectedIds.has(f.id) && !f.deletedAt);
    if (targets.length === 1) setDeleteTarget(targets[0]);
    else setDeleteTargets(targets);
    setContextMenu(null);
  };

  const confirmBulkDelete = async () => {
    if (!deleteTargets || deleteTargets.length === 0) return;
    if (apiAvailable === true) {
      try {
        setIsRefreshing(true);
        for (const item of deleteTargets) {
          await deleteItem(itemPath(item));
        }
        await loadData();
        setSelectedIds(new Set());
        setDeleteTargets(null);
        addToast(`${deleteTargets.length}개 항목을 휴지통으로 이동했습니다`);
      } catch (err: any) {
        addToast(`삭제 실패: ${err.message}`, 'error');
      }
      return;
    }
    // Mock fallback
    const allIds = new Set<string>();
    deleteTargets.forEach((item) => {
      if (item.type === 'folder') getDescendantIds(item.id).forEach((id) => allIds.add(id));
      else allIds.add(item.id);
    });
    const now = new Date().toISOString();
    setAllItems((prev) => prev.map((f) => allIds.has(f.id) ? { ...f, deletedAt: now, updatedAt: now } : f));
    setTrashItems((prev) => [...prev, ...deleteTargets.map((t) => ({ ...t, deletedAt: now }))]);
    if (currentFolderId && allIds.has(currentFolderId)) setCurrentFolderId(null);
    setSelectedIds(new Set());
    setDeleteTargets(null);
    addToast(`${deleteTargets.length}개 항목을 휴지통으로 이동했습니다`);
  };

  const handleDelete = (item: DriveItem) => { setDeleteTarget(item); setContextMenu(null); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (apiAvailable === true) {
      try {
        setIsRefreshing(true);
        await deleteItem(itemPath(deleteTarget));
        await loadData();
        if (deleteTarget.type === 'folder' && currentFolderId === deleteTarget.id) {
          setCurrentFolderId(deleteTarget.parentId);
        }
        setSelectedIds((prev) => { const next = new Set(prev); next.delete(deleteTarget.id); return next; });
        setDeleteTarget(null);
        addToast(`'${deleteTarget.name}' 휴지통으로 이동 완료`);
      } catch (err: any) {
        addToast(`삭제 실패: ${err.message}`, 'error');
      }
      return;
    }
    // Mock fallback
    const idsToRemove = deleteTarget.type === 'folder' ? getDescendantIds(deleteTarget.id) : [deleteTarget.id];
    const now = new Date().toISOString();
    setAllItems((prev) => prev.map((f) => idsToRemove.includes(f.id) ? { ...f, deletedAt: now, updatedAt: now } : f));
    if (deleteTarget.type === 'folder' && currentFolderId === deleteTarget.id) setCurrentFolderId(deleteTarget.parentId);
    setSelectedIds((prev) => { const next = new Set(prev); idsToRemove.forEach((id) => next.delete(id)); return next; });
    setDeleteTarget(null);
    addToast(`'${deleteTarget.name}' 휴지통으로 이동 완료`);
  };

  const handleDownload = (item: DriveItem) => {
    setContextMenu(null);
    if (item.type === 'folder') return;
    if (apiAvailable === true && item.path) {
      const url = getDownloadUrl(item.path);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      addToast(`'${item.name}' 다운로드 시작`);
      return;
    }
    // Mock fallback
    const blob = new Blob([`Mock file content for: ${item.name}`], { type: item.mimeType || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = item.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    addToast(`'${item.name}' 다운로드 시작`);
  };

  const handleRename = (item: DriveItem) => { setContextMenu(null); setRenameTarget(item); };

  const confirmRename = async (newName: string) => {
    if (!renameTarget || !newName.trim()) return;
    if (apiAvailable === true) {
      try {
        await renameItem(itemPath(renameTarget), newName.trim());
        await loadData();
        setRenameTarget(null);
        addToast(`'${renameTarget.name}' → '${newName.trim()}' 이름 변경 완료`);
      } catch (err: any) {
        addToast(`이름 변경 실패: ${err.message}`, 'error');
      }
      return;
    }
    setAllItems((prev) => prev.map((f) =>
      f.id === renameTarget.id ? { ...f, name: newName.trim(), updatedAt: new Date().toISOString() } : f
    ));
    setRenameTarget(null);
    addToast(`'${renameTarget.name}' → '${newName.trim()}' 이름 변경 완료`);
  };

  // === Context Menu ===

  const handleContextMenu = (e: React.MouseEvent, item: DriveItem) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedIds.size > 1 && selectedIds.has(item.id)) {
      setContextMenu({ x: e.clientX, y: e.clientY, item });
    } else {
      setSelectedIds(new Set([item.id]));
      setContextMenu({ x: e.clientX, y: e.clientY, item });
    }
  };

  const handleContentContextMenu = (e: React.MouseEvent) => { e.preventDefault(); setContextMenu(null); };

  // === Upload ===

  const handleUploadClick = () => setShowUploadModal(true);

  const handleFilesSelected = async (fileList: File[]) => {
    setShowUploadModal(false);
    if (fileList.length === 0) return;
    try {
      setIsRefreshing(true);
      const currentPath = currentFolderId
        ? (files.find((f) => f.id === currentFolderId)?.path ?? '')
        : '';
      await uploadFiles(currentPath, fileList);
      await loadData();
      addToast(`${fileList.length}개 파일 업로드 완료`);
    } catch (err: any) {
      addToast(`업로드 실패: ${err.message}`, 'error');
    }
  };

  const handleFolderSelected = async (items: UploadItem[]) => {
    setShowUploadModal(false);
    if (items.length === 0) return;
    try {
      setIsRefreshing(true);
      const currentPath = currentFolderId
        ? (files.find((f) => f.id === currentFolderId)?.path ?? '')
        : '';
      await uploadFolder(currentPath, items);
      await loadData();
      addToast(`${items.length}개 파일 업로드 완료 (폴더 구조 유지)`);
    } catch (err: any) {
      addToast(`폴더 업로드 실패: ${err.message}`, 'error');
    }
  };

  // === New Folder ===

  const handleNewFolderClick = () => { setShowNewFolderModal(true); };

  const handleCreateFolder = async (name: string) => {
    if (!name.trim()) return;
    try {
      const currentPath = currentFolderId
        ? (files.find((f) => f.id === currentFolderId)?.path ?? '')
        : '';
      await createFolderApi(currentPath, name.trim());
      await loadData();
      setShowNewFolderModal(false);
      addToast(`'${name.trim()}' 폴더 생성 완료`);
    } catch (err: any) {
      addToast(`폴더 생성 실패: ${err.message}`, 'error');
    }
  };

  // === Keyboard Shortcuts ===

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      if (e.key === 'Escape') {
        if (contextMenu) { setContextMenu(null); return; }
        if (deleteTarget) { setDeleteTarget(null); return; }
        if (deleteTargets) { setDeleteTargets(null); return; }
        if (selectedIds.size > 0) { setSelectedIds(new Set()); return; }
      }
      if (e.key === 'Delete' || (e.key === 'Backspace' && (e.metaKey || e.ctrlKey))) {
        if (selectedIds.size > 0) { e.preventDefault(); handleDeleteSelected(); return; }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault();
        const currentItems = files.filter((f) => f.parentId === currentFolderId && !f.deletedAt);
        if (currentItems.length > 0) setSelectedIds(new Set(currentItems.map((f) => f.id)));
      }
      if (e.key === 'Enter' && selectedIds.size === 1) {
        const id = Array.from(selectedIds)[0];
        const item = files.find((f) => f.id === id);
        if (item && item.type === 'folder' && !isTrashView) {
          handleFolderClick(item.id);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [contextMenu, deleteTarget, deleteTargets, selectedIds, files, currentFolderId, isTrashView]);

  // === Global Drag & Drop ===

  const handleGlobalDragEnter = (e: React.DragEvent) => {
    if (isTrashView) return;
    e.preventDefault(); e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) setIsDraggingOver(true);
  };

  const handleGlobalDragLeave = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDraggingOver(false); }
  };

  const handleGlobalDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };

  const handleGlobalDrop = async (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    setIsDraggingOver(false);
    dragCounter.current = 0;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const fileList = Array.from(e.dataTransfer.files);
      await handleFilesSelected(fileList);
    }
  };

  const handleItemDragStart = (e: React.DragEvent, item: DriveItem) => {
    if (isTrashView || item.deletedAt) return;
    e.dataTransfer.setData('application/x-drive-item', item.id);
    e.dataTransfer.effectAllowed = 'move';
    const items = selectedIds.size > 1 && selectedIds.has(item.id)
      ? files.filter((f) => selectedIds.has(f.id))
      : [item];
    setDraggedItems(items);
  };

  const handleItemDragEnd = () => {
    setDraggedItems([]);
    setDragOverFolderId(null);
    setDragOverBreadcrumbId(null);
    setDragOverSidebarId(null);
  };

  const moveItemsToFolder = async (folderId: string | null) => {
    if (draggedItems.length === 0) return;
    const invalid = draggedItems.some(
      (item) => item.id === folderId || (item.type === 'folder' && folderId && getDescendantIds(item.id).includes(folderId))
    );
    if (invalid) return;

    try {
      const targetPath = folderId
        ? (files.find((f) => f.id === folderId)?.path ?? '')
        : '';
      setIsRefreshing(true);
      for (const item of draggedItems) {
        await moveItem(itemPath(item), targetPath);
      }
      await loadData();
      setSelectedIds(new Set());
      setDraggedItems([]);
      const targetName = folderId ? files.find((f) => f.id === folderId)?.name ?? '폴더' : '내 드라이브';
      addToast(`${draggedItems.length}개 항목을 '${targetName}'(으)로 이동했습니다`);
    } catch (err: any) {
      addToast(`이동 실패: ${err.message}`, 'error');
    }
  };

  const handleSidebarDragOver = (e: React.DragEvent, folderId: string | null) => {
    if (draggedItems.length === 0) return;
    e.preventDefault(); e.stopPropagation();
    if (folderId) {
      const invalid = draggedItems.some(
        (item) => item.id === folderId || (item.type === 'folder' && getDescendantIds(item.id).includes(folderId))
      );
      if (invalid) { e.dataTransfer.dropEffect = 'none'; return; }
    }
    e.dataTransfer.dropEffect = 'move';
    setDragOverSidebarId(folderId);
  };

  const handleSidebarDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOverSidebarId(null); };

  const handleSidebarDrop = (e: React.DragEvent, folderId: string | null) => {
    e.preventDefault(); e.stopPropagation();
    setDragOverSidebarId(null);
    moveItemsToFolder(folderId);
  };

  const handleFolderDragOver = (e: React.DragEvent, folderId: string) => {
    if (draggedItems.length === 0) return;
    e.preventDefault(); e.stopPropagation();
    const invalid = draggedItems.some(
      (item) => item.id === folderId || (item.type === 'folder' && getDescendantIds(item.id).includes(folderId))
    );
    if (invalid) { e.dataTransfer.dropEffect = 'none'; return; }
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolderId(folderId);
  };

  const handleFolderDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOverFolderId(null); };

  const handleFolderDrop = (e: React.DragEvent, folderId: string) => {
    e.preventDefault(); e.stopPropagation();
    setDragOverFolderId(null);
    moveItemsToFolder(folderId);
  };

  const handleBreadcrumbDragOver = (e: React.DragEvent, folderId: string | null) => {
    if (draggedItems.length === 0) return;
    if (folderId === currentFolderId) return;
    e.preventDefault(); e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverBreadcrumbId(folderId);
  };

  const handleBreadcrumbDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOverBreadcrumbId(null); };

  const handleBreadcrumbDrop = (e: React.DragEvent, folderId: string | null) => {
    e.preventDefault(); e.stopPropagation();
    setDragOverBreadcrumbId(null);
    moveItemsToFolder(folderId);
  };

  const handleSortChange = (field: SortField, order: SortOrder) => { setSortField(field); setSortOrder(order); };



  const breadcrumbPath = getBreadcrumbPath();

  const currentFolderName = isTrashView
    ? '휴지통'
    : currentFolderId
      ? files.find((f) => f.id === currentFolderId && !f.deletedAt)?.name ?? '알 수 없음'
      : '내 드라이브';

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="flex h-screen bg-background-50 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <span className="w-10 h-10 flex items-center justify-center">
            <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin"></i>
          </span>
          <p className="text-sm text-foreground-500">웹드라이브 연결 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-screen bg-background-50 overflow-hidden"
      onDragEnter={handleGlobalDragEnter}
      onDragLeave={handleGlobalDragLeave}
      onDragOver={handleGlobalDragOver}
      onDrop={handleGlobalDrop}
    >
      <Toast toasts={toasts} onRemove={removeToast} />

      {/* API Status banner */}
      {apiAvailable === false && (
        <div className="fixed top-0 left-0 right-0 z-[70] bg-accent-100 border-b border-accent-300 px-4 py-2 flex items-center justify-center gap-2 text-sm">
          <span className="w-4 h-4 flex items-center justify-center text-accent-600">
            <i className="ri-wifi-off-line"></i>
          </span>
          <span className="text-accent-800 font-medium">서버 연결 안 됨</span>
          <span className="text-accent-600">— 목업 모드로 표시 중</span>
          <button
            onClick={() => loadData(true)}
            className="ml-3 px-3 py-1 text-xs font-medium bg-accent-200 text-accent-800 rounded-full hover:bg-accent-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            재연결
          </button>
        </div>
      )}

      {/* Refreshing indicator */}
      {isRefreshing && (
        <div className="fixed top-0 left-0 right-0 z-[65] h-0.5 bg-primary-500/30">
          <div className="h-full bg-primary-500 animate-pulse"></div>
        </div>
      )}

      {/* Global drag overlay */}
      {isDraggingOver && (
        <div className="fixed inset-0 z-[60] bg-primary-500/10 border-4 border-dashed border-primary-400 pointer-events-none flex items-center justify-center animate-in fade-in duration-150">
          <div className="bg-background-50 rounded-2xl px-8 py-6 text-center animate-in zoom-in-95 duration-200">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
              <i className="ri-upload-cloud-2-line text-3xl text-primary-500"></i>
            </span>
            <p className="text-lg font-semibold text-foreground-900">파일을 여기에 놓으세요</p>
            <p className="text-sm text-foreground-500 mt-1">{currentFolderName}에 업로드됩니다</p>
          </div>
        </div>
      )}

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden animate-in fade-in duration-150" onClick={() => setSidebarOpen(false)}></div>
      )}

      {/* Sidebar */}
      <div className={`fixed lg:static inset-y-0 left-0 z-50 w-[260px] lg:w-[240px] transform transition-transform duration-200 lg:transform-none ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <DriveSidebar
          currentFolderId={currentFolderId}
          dragOverFolderId={dragOverSidebarId}
          usedStorage={usedStorage}
          totalStorage={totalStorage}
          onFolderSelect={(id) => {
            if (id === null) { setCurrentFolderId(null); setSearchQuery(''); setSelectedIds(new Set()); }
            else if (id === TRASH_FOLDER_ID) { setCurrentFolderId(TRASH_FOLDER_ID); setSearchQuery(''); setSelectedIds(new Set()); }
            else handleFolderClick(id);
          }}
          allFiles={files}
          onDragOver={handleSidebarDragOver}
          onDragLeave={handleSidebarDragLeave}
          onDrop={handleSidebarDrop}
          onDragEnd={handleItemDragEnd}
        />
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden" ref={mainContentRef}>
        <header className="flex items-center gap-3 px-4 md:px-6 py-3 border-b border-background-200 bg-background-50 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-600 lg:hidden cursor-pointer"
          >
            <i className="ri-menu-line text-lg"></i>
          </button>
          <div className="flex items-center gap-1.5">
            <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${isTrashView ? 'bg-accent-100' : 'bg-primary-100'}`}>
              <i className={`text-lg ${isTrashView ? 'ri-delete-bin-line text-accent-600' : 'ri-hard-drive-2-line text-primary-600'}`}></i>
            </span>
            <h1 className="text-lg font-semibold text-foreground-900">{isTrashView ? '휴지통' : '웹드라이브'}</h1>
          </div>
          <div className="flex-1"></div>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            {isTrashView ? (
              <>
                <span className="bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full text-xs font-medium">{trashItems.length}개 보관 중</span>
                <span className="hidden sm:inline text-foreground-400">{TRASH_RETENTION_DAYS}일 후 자동 삭제</span>
              </>
            ) : (
              <>
                <span className="hidden sm:inline">파일 {totalFileItems.length}개 &middot; 폴더 {totalFolderItems.length}개</span>
                {searchQuery && (
                  <span className="bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full text-xs font-medium">검색: {filteredAndSortedItems.length}개 결과</span>
                )}
              </>
            )}
          </div>
        </header>

        <div className="px-4 md:px-6 border-b border-background-200 bg-background-50 flex-shrink-0">
          <Breadcrumb
            path={breadcrumbPath}
            currentFolderId={currentFolderId}
            dragOverFolderId={dragOverBreadcrumbId}
            onNavigate={setCurrentFolderId}
            onDragOver={handleBreadcrumbDragOver}
            onDragLeave={handleBreadcrumbDragLeave}
            onDrop={handleBreadcrumbDrop}
          />
          <Toolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onUploadClick={handleUploadClick}
            onNewFolderClick={handleNewFolderClick}
            sortField={sortField}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            selectedCount={selectedIds.size}
            onClearSelection={handleClearSelection}
            onDeleteSelected={handleDeleteSelected}
            isTrashView={isTrashView}
            trashCount={trashItems.length}
            onEmptyTrash={emptyTrash}
            onRestoreAllTrash={restoreAllTrash}
          />
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4" onContextMenu={handleContentContextMenu}>
          {viewMode === 'grid' ? (
            <FileGrid
              files={filteredAndSortedItems}
              parentId={currentFolderId}
              selectedItemId={contextMenu?.item.id ?? null}
              selectedIds={selectedIds}
              dragOverFolderId={dragOverFolderId}
              isTrashView={isTrashView}
              onFileClick={handleFileClick}
              onFolderClick={handleFolderClick}
              onContextMenu={handleContextMenu}
              onDragStart={handleItemDragStart}
              onDragEnd={handleItemDragEnd}
              onDragOver={handleFolderDragOver}
              onDragLeave={handleFolderDragLeave}
              onDrop={handleFolderDrop}
              onToggleSelect={handleToggleSelect}
            />
          ) : (
            <FileList
              files={filteredAndSortedItems}
              parentId={currentFolderId}
              selectedItemId={contextMenu?.item.id ?? null}
              selectedIds={selectedIds}
              dragOverFolderId={dragOverFolderId}
              isTrashView={isTrashView}
              onFileClick={handleFileClick}
              onFolderClick={handleFolderClick}
              onContextMenu={handleContextMenu}
              onDragStart={handleItemDragStart}
              onDragEnd={handleItemDragEnd}
              onDragOver={handleFolderDragOver}
              onDragLeave={handleFolderDragLeave}
              onDrop={handleFolderDrop}
              onToggleSelect={handleToggleSelect}
            />
          )}
        </div>
      </main>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          item={contextMenu.item}
          isTrashView={isTrashView}
          onClose={() => setContextMenu(null)}
          onPreview={() => {}}
          onDownload={handleDownload}
          onDelete={handleDelete}
          onRename={handleRename}
          onOpenFolder={handleFolderClick}
          onRestore={restoreFromTrash}
          onPermanentDelete={permanentlyDelete}
        />
      )}

      {/* Single Delete Dialog */}
      {deleteTarget && (
        <DeleteDialog
          item={deleteTarget}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Bulk Delete Dialog */}
      {deleteTargets && deleteTargets.length > 0 && (
        <BulkDeleteDialog
          items={deleteTargets}
          onConfirm={confirmBulkDelete}
          onCancel={() => setDeleteTargets(null)}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadZone
          parentId={currentFolderId}
          parentName={currentFolderName}
          parentPath={currentFolderId ? (files.find((f) => f.id === currentFolderId)?.path ?? '') : ''}
          onClose={() => setShowUploadModal(false)}
          onUpload={(items) => {
            setAllItems((prev) => [...prev, ...items]);
            loadData();
            addToast(`${items.length}개 파일 업로드 완료`);
          }}
        />
      )}

      {/* New Folder Modal */}
      {showNewFolderModal && (
        <NewFolderDialog
          currentFolderName={currentFolderName}
          onConfirm={handleCreateFolder}
          onCancel={() => setShowNewFolderModal(false)}
        />
      )}

      {/* Rename Modal */}
      {renameTarget && (
        <RenameDialog
          initialName={renameTarget.name}
          onConfirm={confirmRename}
          onCancel={() => setRenameTarget(null)}
        />
      )}

      {/* Image Lightbox Viewer */}
      {lightboxIndex !== null && lightboxImages.length > 0 && (
        <ImageViewer
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(idx) => setLightboxIndex(idx)}
        />
      )}

      {/* Media Player (Video/Audio) */}
      {mediaIndex !== null && mediaFiles.length > 0 && (
        <MediaPlayer
          mediaFiles={mediaFiles}
          currentIndex={mediaIndex}
          onClose={() => setMediaIndex(null)}
          onNavigate={(idx) => setMediaIndex(idx)}
        />
      )}

      {/* PDF Viewer */}
      {pdfIndex !== null && pdfItems.length > 0 && (
        <PdfViewer
          pdfFiles={pdfItems}
          currentIndex={pdfIndex}
          onClose={() => setPdfIndex(null)}
          onNavigate={(idx) => setPdfIndex(idx)}
        />
      )}


    </div>
  );
}